import { useState } from 'react';
import Calendar from 'react-calendar';
import 'react-datepicker/dist/react-datepicker.css';

/**
 * Port of `commonComponents/calender.jsx` — the week picker.
 *
 * Kept as-is, including the spelling of the file name and the inline `<style>` block
 * that defines `.highlighted-day` (App.css does not carry that rule, so removing the
 * block would drop the week highlight entirely).
 *
 * Dropped: `ExampleCustomInput`, which returned an empty `<label />` and was passed as
 * react-calendar's `customInput` — a prop react-calendar does not accept, so React never
 * rendered it. It is a leftover from a react-datepicker implementation. The
 * `react-datepicker` CSS import above is NOT dropped: it is the only place that
 * stylesheet is pulled in, and App.css builds on it.
 *
 * `moment` is likewise no longer imported: it was used only for the unused `currentDate`
 * state field.
 */
export function Calender({
  selectedWeekFirstDate,
  getSelectedDate,
}: {
  selectedWeekFirstDate: string;
  getSelectedDate: (isoDate: string) => void;
}): JSX.Element {
  const [startDate, setStartDate] = useState<Date>(new Date(selectedWeekFirstDate));

  /**
   * Highlights the seven days of the selected week.
   *
   * Note the original's `(startDate.getDay() + 7) % 7` is just `getDay()`, and shifting
   * back by it lands on SUNDAY, not the Monday the comment claims. Since `startDate` is
   * already the Monday of the week, `getDay()` is 1 and the highlighted range runs
   * Sunday-to-Saturday — one day out of step with the ISO week the app uses everywhere
   * else. Reproduced exactly: this is what the calendar highlights today.
   */
  const tileClassName = ({ date }: { date: Date }): string | null => {
    const startOfWeek = new Date(startDate);
    startOfWeek.setDate(startDate.getDate() - ((startDate.getDay() + 7) % 7));

    const isCurrentWeekDay =
      date >= startOfWeek && date < new Date(startOfWeek.getTime() + 7 * 24 * 60 * 60 * 1000);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;

    return isCurrentWeekDay ? 'highlighted-day' : isWeekend ? 'weekend-day' : null;
  };

  const handleDateChange = (value: unknown): void => {
    const date = value as Date;
    setStartDate(date);
    getSelectedDate(date.toISOString().split('T')[0] as string);
  };

  // "Next Sunday, plus 28 days" — the same arithmetic as the original, which is a wider
  // bound than mainMenu's own maxNextNavigateDate (now + 28 days).
  const currntDate = new Date();
  const daysUntilSunday = 7 - currntDate.getDay();
  const nextSunday = new Date(currntDate);
  nextSunday.setDate(currntDate.getDate() + daysUntilSunday);

  const maxDate = new Date(nextSunday);
  maxDate.setDate(nextSunday.getDate() + 28);

  const minDate = new Date('2015-01-01');

  return (
    <div>
      <style>{`
          .highlighted-day {
            background-color: #9c9595d6; /* Set your desired background color here */
            color: white; /* Set text color if needed */
          }
        `}</style>
      <Calendar
        value={startDate}
        onChange={handleDateChange}
        tileClassName={tileClassName}
        maxDate={maxDate}
        minDate={minDate}
      />
    </div>
  );
}
